import { TicketLink, TicketQuickViewProvider } from './TicketQuickView';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  STATUS_LABELS,
  TRANSITIONS,
  actionsForMove,
  availableActions,
  type Actor,
  type TicketStatus,
} from '@lixionary-jira/domain';
import { GitBranch, MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, qs } from '../../api/client';
import { useProject } from '../../api/hooks';
import { useMe } from '../../api/session';
import type { BoardColumn } from '../../api/ticket-types';
import type { TicketCard } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { StatusBadge } from '../../ui/Badge';
import { LinkButton } from '../../ui/Button';
import { Alert, QueryView } from '../../ui/feedback';
import { Input, Select } from '../../ui/forms';
import { Modal } from '../../ui/Modal';
import { Avatar, PageHeader, usePageTitle } from '../../ui/misc';
import { shortAge } from '../../ui/Timestamp';
import { useToast } from '../../ui/toast';
import { TicketActionDialog, type DialogAction } from './ActionDialog';
import { ProjectLinks } from '../projects/ProjectLinks';
import { ExternalReporterLabel, PriorityBadge, TicketFlags, TypeLabel } from './bits';

interface BoardData {
  project: { key: string; name: string; archived: boolean };
  columns: BoardColumn[];
}

function actorFor(me: ReturnType<typeof useMe>): Actor {
  return { kind: 'human', userId: me.user.id, role: me.user.role, displayName: me.user.displayName };
}

/** PRJ-02 / NFR-06: five fixed paginated columns with guarded moves and a keyboard alternative to dragging. */
export function BoardPage() {
  return (
    <TicketQuickViewProvider>
      <BoardContent />
    </TicketQuickViewProvider>
  );
}

function BoardContent() {
  const { key } = useParams();
  const project = useProject(key);
  const me = useMe();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  usePageTitle(project.data ? `${project.data.name} · Board` : null);
  useCrumbs([{ label: project.data?.name ?? key ?? '', to: `/projects/${key}` }, { label: 'Board' }]);
  const filters = {
    q: params.get('q') ?? undefined,
    type: params.get('type') ?? undefined,
    priority: params.get('priority') ?? undefined,
    assignee: params.get('assignee') ?? undefined,
    label: params.get('label') ?? undefined,
    blocked: params.get('blocked') ?? undefined,
  };
  const q = useQuery({
    queryKey: ['board', key, filters],
    queryFn: () => api.get<BoardData>(`/projects/${key}/board${qs(filters)}`),
  });
  const [dialog, setDialog] = useState<{ number: number; action: DialogAction } | null>(null);
  const [menu, setMenu] = useState<TicketCard | null>(null);
  const [dragging, setDragging] = useState<TicketCard | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const actor = actorFor(me);
  const archived = !!project.data?.archived;
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    const card = e.active.data.current?.card as TicketCard | undefined;
    const to = e.over?.id as TicketStatus | undefined;
    if (!card || !to || to === card.status) return;
    const allowed = actionsForMove(card.status, to).filter((a) =>
      availableActions(card.status, actor).includes(a),
    );
    if (!allowed.length) {
      toast(`${card.key} cannot move from ${STATUS_LABELS[card.status]} to ${STATUS_LABELS[to]}.`, 'error');
      return;
    }
    setDialog({ number: card.number, action: allowed[0] === 'claim' ? 'start_manual' : allowed[0]! });
  };
  return (
    <div className="page" style={{ maxWidth: 'none' }}>
      <PageHeader
        title="Board"
        subtitle="Five fixed columns. Moves are guarded actions; use a card's menu for keyboard access."
        actions={
          !archived && (
            <LinkButton to={`/projects/${key}/tickets/new`} variant="primary">
              Create ticket
            </LinkButton>
          )
        }
      />
      {project.data && <ProjectLinks links={project.data.links} />}
      {archived && <Alert tone="info">This project is archived. The board is read-only.</Alert>}
      <div className="filter-bar" role="group" aria-label="Board filters">
        <Input
          aria-label="Filter text"
          placeholder="Filter…"
          defaultValue={filters.q}
          onKeyDown={(e) => e.key === 'Enter' && set('q', (e.target as HTMLInputElement).value)}
          style={{ width: 200 }}
        />
        <Select aria-label="Type" value={filters.type ?? ''} onChange={(e) => set('type', e.target.value)}>
          <option value="">All types</option>
          <option value="issue">Issues</option>
          <option value="feature_request">Feature requests</option>
        </Select>
        <Select
          aria-label="Priority"
          value={filters.priority ?? ''}
          onChange={(e) => set('priority', e.target.value)}
        >
          <option value="">All priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </Select>
        <Select
          aria-label="Assignee"
          value={filters.assignee ?? ''}
          onChange={(e) => set('assignee', e.target.value)}
        >
          <option value="">Anyone</option>
          <option value="me">Assigned to me</option>
          <option value="unassigned">Unassigned (AI-claimable)</option>
        </Select>
        <Select
          aria-label="Blocked"
          value={filters.blocked ?? ''}
          onChange={(e) => set('blocked', e.target.value)}
        >
          <option value="">Blocked or not</option>
          <option value="true">Blocked only</option>
          <option value="false">Not blocked</option>
        </Select>
        {[...params.keys()].length > 0 && (
          <button className="btn btn-ghost sm" onClick={() => setParams({}, { replace: true })}>
            Clear filters
          </button>
        )}
      </div>
      <QueryView query={q}>
        {(data) => (
          <DndContext
            sensors={sensors}
            onDragStart={(e) => setDragging(e.active.data.current?.card ?? null)}
            onDragEnd={onDragEnd}
            onDragCancel={() => setDragging(null)}
          >
            <div className="board">
              {data.columns.map((col) => (
                <Column
                  key={col.status}
                  projectKey={key!}
                  column={col}
                  filters={filters}
                  dragging={dragging}
                  actor={actor}
                  disabled={archived}
                  onMenu={setMenu}
                />
              ))}
            </div>
          </DndContext>
        )}
      </QueryView>
      {menu && (
        <MoveMenu
          card={menu}
          actor={actor}
          projectKey={key!}
          onClose={() => setMenu(null)}
          onPick={(action) => {
            setMenu(null);
            setDialog({ number: menu.number, action });
          }}
        />
      )}
      {dialog && (
        <TicketActionDialog
          projectKey={key!}
          number={dialog.number}
          action={dialog.action}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

function Column({
  projectKey,
  column,
  filters,
  dragging,
  actor,
  disabled,
  onMenu,
}: {
  projectKey: string;
  column: BoardColumn;
  filters: Record<string, string | undefined>;
  dragging: TicketCard | null;
  actor: Actor;
  disabled: boolean;
  onMenu: (c: TicketCard) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.status, disabled });
  const valid =
    dragging && dragging.status !== column.status
      ? actionsForMove(dragging.status, column.status).some((a) =>
          availableActions(dragging.status, actor).includes(a),
        )
      : null;
  const more = useInfiniteQuery({
    queryKey: ['board-more', projectKey, column.status, filters],
    queryFn: ({ pageParam }) =>
      api.get<{ columns: BoardColumn[] }>(
        `/projects/${projectKey}/board${qs({ ...filters, column: column.status, offset: pageParam })}`,
      ),
    initialPageParam: column.nextOffset ?? 0,
    getNextPageParam: (last) => last.columns[0]?.nextOffset ?? undefined,
    enabled: false,
  });
  const isClosed = column.status === 'closed';
  const [closedLimit, setClosedLimit] = useState(5);

  const extra = more.data?.pages.flatMap((p) => p.columns[0]?.cards ?? []) ?? [];
  const allCards = [...column.cards, ...extra];
  const cards = isClosed ? allCards.slice(0, closedLimit) : allCards;

  const hasMoreServer = more.data ? !!more.hasNextPage : column.nextOffset !== null;
  const hasMore = isClosed ? cards.length < column.total : hasMoreServer;

  const handleLoadMore = () => {
    if (isClosed) {
      const nextLimit = closedLimit + 5;
      setClosedLimit(nextLimit);
      if (nextLimit > allCards.length && hasMoreServer) {
        if (more.data) {
          void more.fetchNextPage();
        } else {
          void more.refetch();
        }
      }
    } else {
      if (more.data) {
        void more.fetchNextPage();
      } else {
        void more.refetch();
      }
    }
  };

  return (
    <section
      ref={setNodeRef}
      className={`column${isOver && valid ? ' drop-target' : ''}${dragging && valid === false ? ' drop-invalid' : ''}`}
      aria-label={`${STATUS_LABELS[column.status]} column, ${column.total} tickets`}
    >
      <div className="column-header">
        <span>{STATUS_LABELS[column.status]}</span>
        <span className="mono muted">{column.total}</span>
      </div>
      <div className="column-body">
        {cards.length === 0 && (
          <span className="small muted" style={{ padding: 8 }}>
            No tickets
          </span>
        )}
        {cards.map((c) => (
          <Card key={c.id} card={c} projectKey={projectKey} onMenu={onMenu} disabled={disabled} />
        ))}
        {hasMore && (
          <button className="btn btn-ghost sm" onClick={handleLoadMore} disabled={more.isFetching}>
            {more.isFetching ? 'Loading…' : `Load more (${column.total - cards.length})`}
          </button>
        )}
      </div>
    </section>
  );
}

function Card({
  card,
  projectKey,
  onMenu,
  disabled,
}: {
  card: TicketCard;
  projectKey: string;
  onMenu: (c: TicketCard) => void;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: card.id,
    data: { card },
    disabled,
  });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 10,
        position: 'relative' as const,
      }
    : undefined;
  const person = card.owner ?? card.assignee;
  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`ticket-card${isDragging ? ' dragging' : ''}`}
      {...listeners}
      {...attributes}
      role="group"
      aria-roledescription="Draggable ticket"
      aria-label={`${card.key} ${card.title}`}
    >
      <div className="row between nowrap">
        <TicketLink
          projectKey={projectKey}
          number={card.number}
          className="mono small"
          onKeyDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {card.key}
        </TicketLink>
        {!disabled && (
          <button
            className="icon-btn"
            style={{ width: 26, height: 26 }}
            aria-label={`Move ${card.key}`}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={() => onMenu(card)}
          >
            <MoreHorizontal size={14} />
          </button>
        )}
      </div>
      <TicketLink
        projectKey={projectKey}
        number={card.number}
        className="title"
        onKeyDown={(e) => e.stopPropagation()}
        style={{ color: 'var(--color-ink)' }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {card.title}
      </TicketLink>
      <div className="meta">
        <TypeLabel type={card.type} />
        <PriorityBadge priority={card.priority} />
        {card.status === 'closed' && <StatusBadge status={card.status} resolution={card.resolution} />}
      </div>
      <div className="meta">
        <TicketFlags t={card} />
      </div>
      {card.externalReporter && (
        <div className="meta">
          <ExternalReporterLabel reporter={card.externalReporter} />
        </div>
      )}
      <div className="meta">
        {person ? (
          <span className="row nowrap" style={{ gap: 6 }}>
            <Avatar name={person.name} kind={person.kind} small />
            {person.name}
            {card.owner ? '' : ' (assigned)'}
          </span>
        ) : (
          <span>{card.status === 'todo' ? 'AI-claimable' : 'Unassigned'}</span>
        )}
        {card.parentKey && (
          <span className="row nowrap" style={{ gap: 3 }}>
            <GitBranch size={12} aria-hidden /> {card.parentKey}
          </span>
        )}
        <span
          title={`In this column since ${new Date(card.statusChangedAt).toLocaleString()}`}
          style={{ marginLeft: 'auto' }}
        >
          {shortAge(card.statusChangedAt)}
        </span>
      </div>
    </article>
  );
}

function MoveMenu({
  card,
  actor,
  projectKey,
  onClose,
  onPick,
}: {
  card: TicketCard;
  actor: Actor;
  projectKey: string;
  onClose: () => void;
  onPick: (a: DialogAction) => void;
}) {
  const actions = availableActions(card.status, actor).filter(
    (a) => a !== 'close_done' && a !== 'mark_ready',
  );
  return (
    <Modal
      title={`Move ${card.key}`}
      onClose={onClose}
      actions={
        <button className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
      }
    >
      <p className="muted">
        Currently {STATUS_LABELS[card.status]}. Only actions you are allowed to take are listed.
      </p>
      {actions.length === 0 ? (
        <Alert tone="info">
          No status actions are available to you here.{' '}
          {actor.kind === 'human' && actor.role !== 'admin'
            ? 'Admins accept, reject and move work; you can discuss it on the ticket.'
            : ''}
        </Alert>
      ) : (
        <div className="stack sm">
          {actions.map((a) => (
            <button
              key={a}
              className="btn btn-secondary block"
              style={{ justifyContent: 'space-between' }}
              onClick={() => onPick(a === 'claim' ? 'start_manual' : a)}
              data-autofocus={a === actions[0] || undefined}
            >
              <span>{a === 'claim' ? 'Take manual ownership' : TRANSITIONS[a].label}</span>
              <span className="muted small">→ {STATUS_LABELS[TRANSITIONS[a].to]}</span>
            </button>
          ))}
        </div>
      )}
      <Link to={`/projects/${projectKey}/tickets/${card.number}`}>Open {card.key}</Link>
    </Modal>
  );
}
